import { api } from '@/features/auth';

import {
  CreateProjectRequest,
  GetProjectRequest,
  GetProjectResponse,
  UpdateProjectRequest,
  UpdateProjectTechnologyItemRequest,
  UpdateProjectTechnologyRequest,
  DeleteProjectTechnologyItemRequest,
  UpdateProjectItemRequest,
} from './types';

const projectFields = [
  'id',
  'created_at',
  'created_by',
  'updated_at',
  'updated_by',
  'status',
  'company',
  'title',
  'country',
  'city',
  'other_details',
  'permits_comments',

  //TODO: Get only specific data:
  'technologies.*',
  'technologies.item.*',
  'technologies.permits.*',
  'technologies.files.file.*',

  //TODO: Get only specific data:
  'permits.*',
  'connection_infrastructure.*',
  'land_rights.*',
  'capex_and_opex.*',
  'other_info.*',
  'portfolio_summary.*',
  'portfolio_summary.technologies.*',
  'portfolio_summary.markets.*',
  'financial_highlights.*',
  'team_and_governance.*',
];

export const projectsApi = api.injectEndpoints({
  endpoints: (builder) => ({

    getProject: builder.query<GetProjectResponse['data'], GetProjectRequest>({
      query: ({ id }: GetProjectRequest) => ({
        url: `items/projects/${id}`,
        method: 'GET',
        params: {
          fields: projectFields,
        },
      }),
      transformResponse: ({ data }: GetProjectResponse) => data,
      providesTags: (_result, _error, args) => [{ type: 'Projects', id: args.id }],
    }),

    getProjectsByIds: builder.query<GetProjectResponse['data'][], string[]>({
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const key = [...new Set(queryArgs)].sort().join(',');
        return `${endpointName}|${key}`;
      },
      query: (ids) => {
        const unique = [...new Set(ids)].filter(Boolean);
        return {
          url: `items/projects`,
          method: 'GET',
          params: {
            fields: projectFields,
            filter: JSON.stringify({ id: { _in: unique } }),
            limit: Math.max(unique.length, 1),
          },
        };
      },
      transformResponse: (resp: { data?: GetProjectResponse['data'][] }) => resp?.data ?? [],
      providesTags: (result) =>
        result
          ? [...result.map((p) => ({ type: 'Projects' as const, id: p.id })), { type: 'Projects' as const, id: 'LIST' }]
          : [{ type: 'Projects' as const, id: 'LIST' }],
    }),

    createProject: builder.mutation<GetProjectResponse['data'], CreateProjectRequest>({
      query: (data: CreateProjectRequest) => ({
        url: `items/projects`,
        method: 'POST',
        body: data,
      }),
      transformResponse: ({ data }: GetProjectResponse) => data,
      invalidatesTags: [{ type: 'Projects', id: 'me' }],
    }),

    updateProject: builder.mutation<void, UpdateProjectRequest>({
      query: ({ id, data }) => ({
        url: `items/projects/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: 'Projects', id: 'me' },
        { type: 'Projects', id: args.id },
        { type: 'Opportunities', id: 'me' },
        { type: 'Opportunities', id: 'published' },
      ],
    }),

    updateProjectTechnology: builder.mutation<void, UpdateProjectTechnologyRequest>({
      query: ({ id, data }: UpdateProjectTechnologyRequest) => ({
        url: `items/projects_technologies/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: 'Projects', id: 'me' },
        { type: 'Projects', id: args.projectId },
        { type: 'Opportunities', id: args.opportunityId },
      ],
    }),

    updateProjectItem: builder.mutation<void, UpdateProjectItemRequest>({
      query: ({ id, data }) => ({
        url: `items/projects/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: 'Projects', id: 'me' },
        { type: 'Projects', id: args.projectId },
        { type: 'Opportunities', id: args.opportunityId },
      ],
    }),

    updateProjectTechnologyItem: builder.mutation<void, UpdateProjectTechnologyItemRequest>({
      query: ({ collection, data: { id, ...data } }: UpdateProjectTechnologyItemRequest) => ({
        url: `items/${collection}/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: 'Projects', id: 'me' },
        { type: 'Projects', id: args.projectId },
        { type: 'Opportunities', id: args.opportunityId },
      ],
    }),

    deleteProjectTechnologyItem: builder.mutation<void, DeleteProjectTechnologyItemRequest>({
      query: ({ id }) => ({
        url: `items/projects_technologies_permits/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: 'Projects', id: 'me' },
        { type: 'Projects', id: args.projectId },
      ],
    }),

    deleteProjectPermit: builder.mutation<void, DeleteProjectTechnologyItemRequest>({
      query: ({ id }) => ({
        url: `items/project_permits/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: 'Projects', id: 'me' },
        { type: 'Projects', id: args.projectId },
      ],
    }),
  }),
});

export const {
  useGetProjectQuery,
  useGetProjectsByIdsQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useUpdateProjectTechnologyMutation,
  useUpdateProjectTechnologyItemMutation,
  useUpdateProjectItemMutation,
  useDeleteProjectTechnologyItemMutation,
  useDeleteProjectPermitMutation,
} = projectsApi;

export * from './types';
